import { useState, useEffect, useCallback } from 'react'
import { AdminAlert, AdminButton, isNotBuiltYet } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { adminGetRevenueOverview, adminUpdateStakingFeeRate } from '../../../services/admin'

function fmtAmount(amount, opts = {}) {
  const n = Number(amount)
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2, ...opts }) : amount ?? '—'
}

function RevenueCard({ label, value, sub, accent }) {
  return (
    <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">{label}</p>
      <p className={`font-extrabold text-[26px] leading-none ${accent ? 'text-primary-container' : ''}`}>
        {value}
      </p>
      {sub && <p className="text-[11px] text-on-surface-variant">{sub}</p>}
    </div>
  )
}

export function PlatformRevenue() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [feeRate, setFeeRate] = useState('')
  const [savedFeeRate, setSavedFeeRate] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    return adminGetRevenueOverview()
      .then(res => {
        setData(res)
        const rate = res?.staking?.feeRatePercent ?? 0
        setFeeRate(String(rate))
        setSavedFeeRate(String(rate))
      })
      .catch(err => { if (!isNotBuiltYet(err)) setError(err.message) })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const isDirty = Number(feeRate) !== Number(savedFeeRate)

  const handleSaveFee = async () => {
    const rate = Number(feeRate)
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      setSaveError('Staking fee rate must be a number between 0 and 100.')
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      await adminUpdateStakingFeeRate(rate)
      setSuccessMsg('Staking fee rate updated.')
      setTimeout(() => setSuccessMsg(null), 3000)
      await load()
    } catch (err) {
      setSaveError(err.message ?? 'Failed to update staking fee rate')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
    )
  }

  const staking = data?.staking ?? {}
  const offerwall = data?.offerwall ?? {}
  const advertising = data?.advertising ?? {}

  return (
    <div className="flex flex-col gap-8">
      {error && <AdminAlert type="error" message={error} />}

      {/* ── Staking revenue ── */}
      <div>
        <h2 className="font-extrabold text-[18px] uppercase tracking-widest mb-1">Staking Revenue</h2>
        <p className="text-[12px] text-on-surface-variant mb-4">
          Platform revenue skimmed from staking, based on total CLT staked and the fee rate below.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <RevenueCard label="Total Staked" value={`${fmtAmount(staking.totalStaked)} CLT`} />
          <RevenueCard label="Fee Rate" value={`${fmtAmount(staking.feeRatePercent)}%`} />
          <RevenueCard label="Staking Revenue" value={`${fmtAmount(staking.revenue)} CLT`} accent />
          <RevenueCard label="Currency" value={staking.currency ?? 'CLT'} />
        </div>
      </div>

      {/* ── Staking fee rate control ── */}
      <div className="border-4 border-on-background bg-surface p-5 flex flex-col gap-4"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <h3 className="font-extrabold text-[13px] uppercase tracking-wider">Staking Fee Rate</h3>
        <p className="text-[12px] text-on-surface-variant">
          The percentage of recorded stake amounts counted as platform staking revenue (0–100).
        </p>
        {saveError && <AdminAlert type="error" message={saveError} />}
        {successMsg && <AdminAlert type="success" message={successMsg} />}
        <div className="flex flex-col gap-1 max-w-xs">
          <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Fee Rate (%)</label>
          <input
            type="number" min="0" max="100" step="0.1"
            value={feeRate}
            onChange={e => setFeeRate(e.target.value)}
            className="bg-surface border-2 border-on-background px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container"
          />
        </div>
        <div className="flex gap-3 max-w-md">
          <div className="flex-1">
            <AdminButton onClick={handleSaveFee} loading={saving} disabled={!isDirty} variant="gold">
              <span className="material-symbols-outlined text-[16px]">save</span>
              Save Fee Rate
            </AdminButton>
          </div>
          <div className="w-40">
            <AdminButton onClick={() => setFeeRate(savedFeeRate)} disabled={!isDirty || saving} variant="outline">
              Discard
            </AdminButton>
          </div>
        </div>
      </div>

      {/* ── Offerwall revenue ── */}
      <div>
        <h2 className="font-extrabold text-[18px] uppercase tracking-widest mb-1">Offerwall Revenue</h2>
        <p className="text-[12px] text-on-surface-variant mb-4">
          Provider-reported payments from AdGem and CPX Research offerwall completions.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <RevenueCard label="AdGem" value={fmtAmount(offerwall.adgem?.amount)} sub="provider-reported" />
          <RevenueCard label="CPX (USD)" value={`$${fmtAmount(offerwall.cpx?.amountUsd)}`} />
          <RevenueCard label="CPX (CIS Rewarded)" value={fmtAmount(offerwall.cpx?.rewardCis)} />
        </div>
      </div>

      {/* ── Advertising revenue ── */}
      <div>
        <h2 className="font-extrabold text-[18px] uppercase tracking-widest mb-1">Advertising Revenue</h2>
        <p className="text-[12px] text-on-surface-variant mb-4">
          Payments received from advertisers for approved/active ad slots, by pay token.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <RevenueCard label="ETH Paid" value={fmtAmount(advertising.eth)} />
          <RevenueCard label="CLT Paid" value={fmtAmount(advertising.clt)} />
        </div>
      </div>
    </div>
  )
}